import { TypesApi } from '@/api/types';
import { AdditionalDocumentType } from '@/shared/types/additional-document-type';
import { AppointmentCancellationType } from '@/shared/types/appointment-cancellation-type';
import { AppointmentExecutorType } from '@/shared/types/appointment-executor-type';
import { AppointmentNoInstructionType } from '@/shared/types/appointment-no-instruction-type';
import { AppointmentOutcomeType } from '@/shared/types/appointment-outcome-type';
import { AppointmentPaymentStatusType } from '@/shared/types/appointment-payment-status-type';
import { AppointmentProductType } from '@/shared/types/appointment-product-type';
import { AppointmentReasonType } from '@/shared/types/appointment-reason-type';
import { AppointmentType } from '@/shared/types/appointment-type';
import { ClientOriginalSourceType } from '@/shared/types/client-original-source-type';
import { ClientRelationshipType } from '@/shared/types/client-relationship-type';
import { ClientStatusType } from '@/shared/types/client-status-type';
import { ClientTitleType } from '@/shared/types/client-title-type';
import { InteractionLetterType } from '@/shared/types/interaction-letter-type';
import { InteractionOutcomeType } from '@/shared/types/interaction-outcome-type';
import { InteractionType } from '@/shared/types/interaction-type';
import { PowerOfAttorneyType } from '@/shared/types/power-of-attorney-type';
import { WillType } from '@/shared/types/will-type';
import { State } from '@/stores/types/state';
import { camelCase } from '@/string-utilities';
import { ActionContext, ActionTree } from 'vuex';

const api = new TypesApi();

export class Actions implements ActionTree<State, any> {
    [key: string]: ((injectee: ActionContext<State, any>, payload: any) => any);

    public createTypeAsync = async (context: ActionContext<State, any>, payload: { type: string, item: object }) => {

        const formattedType =
            camelCase(`create ${payload.type}${(!payload.type.toLowerCase().endsWith('type') ? 'Type' : '')} Async`);
        await context.dispatch(formattedType, payload.item);
    }

    public updateTypeAsync = async (context: ActionContext<State, any>, payload: { type: string, item: object }) => {
        const formattedType =
            camelCase(`update ${payload.type}${(!payload.type.toLowerCase().endsWith('type') ? 'Type' : '')} Async`);
        await context.dispatch(formattedType, payload.item);
    }

    public archiveTypeAsync = async (context: ActionContext<State, any>, payload: { type: string, item: object }) => {
        const formattedType =
            camelCase(`archive ${payload.type}${(!payload.type.toLowerCase().endsWith('type') ? 'Type' : '')} Async`);
        await context.dispatch(formattedType, payload.item);
    }

    public loadTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getTypesAsync();
        context.commit('setAdditionalDocumentTypes', types.additionalDocumentTypes);
        context.commit('setAppointmentCancellationTypes', types.appointmentCancellationTypes);
        context.commit('setAppointmentExecutorTypes', types.appointmentExecutorTypes);
        context.commit('setAppointmentNoInstructionTypes', types.appointmentNoInstructionTypes);
        context.commit('setAppointmentOutcomeTypes', types.appointmentOutcomeTypes);
        context.commit('setAppointmentPaymentStatusTypes', types.appointmentPaymentStatusTypes);
        context.commit('setAppointmentProductTypes', types.appointmentProductTypes);
        context.commit('setAppointmentReasonTypes', types.appointmentReasonTypes);
        context.commit('setAppointmentTypes', types.appointmentTypes);
        context.commit('setClientOriginalSourceTypes', types.clientOriginalSourceTypes);
        context.commit('setClientRelationshipTypes', types.clientRelationshipTypes);
        context.commit('setClientStatusTypes', types.clientStatusTypes);
        context.commit('setClientTitleTypes', types.clientTitleTypes);
        context.commit('setInteractionLetterTypes', types.interactionLetterTypes);
        context.commit('setInteractionOutcomeTypes', types.interactionOutcomeTypes);
        context.commit('setInteractionTypes', types.interactionTypes);
        context.commit('setPowerOfAttorneyTypes', types.powerOfAttorneyTypes);
        context.commit('setWillTypes', types.willTypes);
    }

    public loadAdditionalDocumentTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getAdditionalDocumentTypesAsync();
        context.commit('setAdditionalDocumentTypes', types);
    }

    public createAdditionalDocumentTypeAsync = async (context: ActionContext<State, any>, type: AdditionalDocumentType) => {
        await api.createAdditionalDocumentTypeAsync(type);
    }

    public updateAdditionalDocumentTypeAsync = async (context: ActionContext<State, any>, type: AdditionalDocumentType) => {
        await api.updateAdditionalDocumentTypeAsync(type);
    }

    public archiveAdditionalDocumentTypeAsync = async (context: ActionContext<State, any>, type: AdditionalDocumentType) => {
        await api.archiveAdditionalDocumentTypeAsync(type);
    }

    public loadAppointmentCancellationTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getAppointmentCancellationTypesAsync();
        context.commit('setAppointmentCancellationTypes', types);
    }

    public createAppointmentCancellationTypeAsync = async (context: ActionContext<State, any>, type: AppointmentCancellationType) => {
        await api.createAppointmentCancellationTypeAsync(type);
    }

    public updateAppointmentCancellationTypeAsync = async (context: ActionContext<State, any>, type: AppointmentCancellationType) => {
        await api.updateAppointmentCancellationTypeAsync(type);
    }

    public archiveAppointmentCancellationTypeAsync = async (context: ActionContext<State, any>, type: AppointmentCancellationType) => {
        await api.archiveAppointmentCancellationTypeAsync(type);
    }

    public loadAppointmentExecutorTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getAppointmentExecutorTypesAsync();
        context.commit('setAppointmentExecutorTypes', types);
    }

    public createAppointmentExecutorTypeAsync = async (context: ActionContext<State, any>, type: AppointmentExecutorType) => {
        await api.createAppointmentExecutorTypeAsync(type);
    }

    public updateAppointmentExecutorTypeAsync = async (context: ActionContext<State, any>, type: AppointmentExecutorType) => {
        await api.updateAppointmentExecutorTypeAsync(type);
    }

    public archiveAppointmentExecutorTypeAsync = async (context: ActionContext<State, any>, type: AppointmentExecutorType) => {
        await api.archiveAppointmentExecutorTypeAsync(type);
    }

    public loadAppointmentReasonTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getAppointmentReasonTypesAsync();
        context.commit('setAppointmentReasonTypes', types);
    }

    public createAppointmentReasonTypeAsync = async (context: ActionContext<State, any>, type: AppointmentReasonType) => {
        await api.createAppointmentReasonTypeAsync(type);
    }

    public updateAppointmentReasonTypeAsync = async (context: ActionContext<State, any>, type: AppointmentReasonType) => {
        await api.updateAppointmentReasonTypeAsync(type);
    }

    public archiveAppointmentReasonTypeAsync = async (context: ActionContext<State, any>, type: AppointmentReasonType) => {
        await api.archiveAppointmentReasonTypeAsync(type);
    }

    public loadAppointmentNoInstructionTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getAppointmentNoInstructionTypesAsync();
        context.commit('setAppointmentNoInstructionTypes', types);
    }

    public createAppointmentNoInstructionTypeAsync = async (context: ActionContext<State, any>, type: AppointmentNoInstructionType) => {
        await api.createAppointmentNoInstructionTypeAsync(type);
    }

    public updateAppointmentNoInstructionTypeAsync = async (context: ActionContext<State, any>, type: AppointmentNoInstructionType) => {
        await api.updateAppointmentNoInstructionTypeAsync(type);
    }

    public archiveAppointmentNoInstructionTypeAsync = async (context: ActionContext<State, any>, type: AppointmentNoInstructionType) => {
        await api.archiveAppointmentNoInstructionTypeAsync(type);
    }

    public loadAppointmentOutcomeTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getAppointmentOutcomeTypesAsync();
        context.commit('setAppointmentOutcomeTypes', types);
    }

    public createAppointmentOutcomeTypeAsync = async (context: ActionContext<State, any>, type: AppointmentOutcomeType) => {
        await api.createAppointmentOutcomeTypeAsync(type);
    }

    public updateAppointmentOutcomeTypeAsync = async (context: ActionContext<State, any>, type: AppointmentOutcomeType) => {
        await api.updateAppointmentOutcomeTypeAsync(type);
    }

    public archiveAppointmentOutcomeTypeAsync = async (context: ActionContext<State, any>, type: AppointmentOutcomeType) => {
        await api.archiveAppointmentOutcomeTypeAsync(type);
    }

    public loadAppointmentPaymentStatusTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getAppointmentPaymentStatusTypesAsync();
        context.commit('setAppointmentPaymentStatusTypes', types);
    }

    public createAppointmentPaymentStatusTypeAsync = async (context: ActionContext<State, any>, type: AppointmentPaymentStatusType) => {
        await api.createAppointmentPaymentStatusTypeAsync(type);
    }

    public updateAppointmentPaymentStatusTypeAsync = async (context: ActionContext<State, any>, type: AppointmentPaymentStatusType) => {
        await api.updateAppointmentPaymentStatusTypeAsync(type);
    }

    public archiveAppointmentPaymentStatusTypeAsync = async (context: ActionContext<State, any>, type: AppointmentPaymentStatusType) => {
        await api.archiveAppointmentPaymentStatusTypeAsync(type);
    }

    public loadAppointmentProductTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getAppointmentProductTypesAsync();
        context.commit('setAppointmentProductTypes', types);
    }

    public createAppointmentProductTypeAsync = async (context: ActionContext<State, any>, type: AppointmentProductType) => {
        await api.createAppointmentProductTypeAsync(type);
    }

    public updateAppointmentProductTypeAsync = async (context: ActionContext<State, any>, type: AppointmentProductType) => {
        await api.updateAppointmentProductTypeAsync(type);
    }

    public archiveAppointmentProductTypeAsync = async (context: ActionContext<State, any>, type: AppointmentProductType) => {
        await api.archiveAppointmentProductTypeAsync(type);
    }

    public loadAppointmentTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getAppointmentTypesAsync();
        context.commit('setAppointmentTypes', types);
    }

    public createAppointmentTypeAsync = async (context: ActionContext<State, any>, type: AppointmentType) => {
        await api.createAppointmentTypeAsync(type);
    }

    public updateAppointmentTypeAsync = async (context: ActionContext<State, any>, type: AppointmentType) => {
        await api.updateAppointmentTypeAsync(type);
    }

    public archiveAppointmentTypeAsync = async (context: ActionContext<State, any>, type: AppointmentType) => {
        await api.archiveAppointmentTypeAsync(type);
    }

    public loadClientOriginalSourceTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getClientOriginalSourceTypesAsync();
        context.commit('setClientOriginalSourceTypes', types);
    }

    public createClientOriginalSourceTypeAsync = async (context: ActionContext<State, any>, type: ClientOriginalSourceType) => {
        await api.createClientOriginalSourceTypeAsync(type);
    }

    public updateClientOriginalSourceTypeAsync = async (context: ActionContext<State, any>, type: ClientOriginalSourceType) => {
        await api.updateClientOriginalSourceTypeAsync(type);
    }

    public archiveClientOriginalSourceTypeAsync = async (context: ActionContext<State, any>, type: ClientOriginalSourceType) => {
        await api.archiveClientOriginalSourceTypeAsync(type);
    }

    public loadClientRelationshipTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getClientRelationshipTypesAsync();
        context.commit('setClientRelationshipTypes', types);
    }

    public createClientRelationshipTypeAsync = async (context: ActionContext<State, any>, type: ClientRelationshipType) => {
        await api.createClientRelationshipTypeAsync(type);
    }

    public updateClientRelationshipTypeAsync = async (context: ActionContext<State, any>, type: ClientRelationshipType) => {
        await api.updateClientRelationshipTypeAsync(type);
    }

    public archiveClientRelationshipTypeAsync = async (context: ActionContext<State, any>, type: ClientRelationshipType) => {
        await api.archiveClientRelationshipTypeAsync(type);
    }

    public loadClientStatusTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getClientStatusTypesAsync();
        context.commit('setClientStatusTypes', types);
    }

    public createClientStatusTypeAsync = async (context: ActionContext<State, any>, type: ClientStatusType) => {
        await api.createClientStatusTypeAsync(type);
    }

    public updateClientStatusTypeAsync = async (context: ActionContext<State, any>, type: ClientStatusType) => {
        await api.updateClientStatusTypeAsync(type);
    }

    public archiveClientStatusTypeAsync = async (context: ActionContext<State, any>, type: ClientStatusType) => {
        await api.archiveClientStatusTypeAsync(type);
    }

    public loadClientTitleTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getClientTitleTypesAsync();
        context.commit('setClientTitleTypes', types);
    }

    public createClientTitleTypeAsync = async (context: ActionContext<State, any>, type: ClientTitleType) => {
        await api.createClientTitleTypeAsync(type);
    }

    public updateClientTitleTypeAsync = async (context: ActionContext<State, any>, type: ClientTitleType) => {
        await api.updateClientTitleTypeAsync(type);
    }

    public archiveClientTitleTypeAsync = async (context: ActionContext<State, any>, type: ClientTitleType) => {
        await api.archiveClientTitleTypeAsync(type);
    }

    public loadInteractionLetterTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getInteractionLetterTypesAsync();
        context.commit('setInteractionLetterTypes', types);
    }

    public createInteractionLetterTypeAsync = async (context: ActionContext<State, any>, type: InteractionLetterType) => {
        await api.createInteractionLetterTypeAsync(type);
    }

    public updateInteractionLetterTypeAsync = async (context: ActionContext<State, any>, type: InteractionLetterType) => {
        await api.updateInteractionLetterTypeAsync(type);
    }

    public archiveInteractionLetterTypeAsync = async (context: ActionContext<State, any>, type: InteractionLetterType) => {
        await api.archiveInteractionLetterTypeAsync(type);
    }

    public loadInteractionOutcomeTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getInteractionOutcomeTypesAsync();
        context.commit('setInteractionOutcomeTypes', types);
    }

    public createInteractionOutcomeTypeAsync = async (context: ActionContext<State, any>, type: InteractionOutcomeType) => {
        await api.createInteractionOutcomeTypeAsync(type);
    }

    public updateInteractionOutcomeTypeAsync = async (context: ActionContext<State, any>, type: InteractionOutcomeType) => {
        await api.updateInteractionOutcomeTypeAsync(type);
    }

    public archiveInteractionOutcomeTypeAsync = async (context: ActionContext<State, any>, type: InteractionOutcomeType) => {
        await api.archiveInteractionOutcomeTypeAsync(type);
    }

    public loadInteractionTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getInteractionTypesAsync();
        context.commit('setInteractionTypes', types);
    }

    public createInteractionTypeAsync = async (context: ActionContext<State, any>, type: InteractionType) => {
        await api.createInteractionTypeAsync(type);
    }

    public updateInteractionTypeAsync = async (context: ActionContext<State, any>, type: InteractionType) => {
        await api.updateInteractionTypeAsync(type);
    }

    public archiveInteractionTypeAsync = async (context: ActionContext<State, any>, type: InteractionType) => {
        await api.archiveInteractionTypeAsync(type);
    }

    public loadPartnerPracticeTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getPartnerPracticeTypesAsync();
        context.commit('setPartnerPracticeTypes', types);
    }

    public loadPowerOfAttorneyTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getPowerOfAttorneyTypesAsync();
        context.commit('setPowerOfAttorneyTypes', types);
    }

    public createPowerOfAttorneyTypeAsync = async (context: ActionContext<State, any>, type: PowerOfAttorneyType) => {
        await api.createPowerOfAttorneyTypeAsync(type);
    }

    public updatePowerOfAttorneyTypeAsync = async (context: ActionContext<State, any>, type: PowerOfAttorneyType) => {
        await api.updatePowerOfAttorneyTypeAsync(type);
    }

    public archivePowerOfAttorneyTypeAsync = async (context: ActionContext<State, any>, type: PowerOfAttorneyType) => {
        await api.archivePowerOfAttorneyTypeAsync(type);
    }

    public loadWillTypesAsync = async (context: ActionContext<State, any>) => {
        const types = await api.getWillTypesAsync();
        context.commit('setWillTypes', types);
    }

    public createWillTypeAsync = async (context: ActionContext<State, any>, type: WillType) => {
        await api.createWillTypeAsync(type);
    }

    public updateWillTypeAsync = async (context: ActionContext<State, any>, type: WillType) => {
        await api.updateWillTypeAsync(type);
    }

    public archiveWillTypeAsync = async (context: ActionContext<State, any>, type: WillType) => {
        await api.archiveWillTypeAsync(type);
    }
}
